import { z } from 'zod'
import { listSchema, zId, zIsoDate, zJsonBlob } from '../common'

// Problems reported by an associated property (e.g. the esim-api catalog) to
// the platform. One alert = (property, key): re-reporting an open key refreshes
// it (lastSeenAt, occurrences) instead of duplicating it.
//   open     → still firing; counted in the summary and the global banner.
//   resolved → closed, either by the property itself or manually by an admin.
export const ALERT_STATUSES = ['open', 'resolved'] as const
export type AlertStatus = (typeof ALERT_STATUSES)[number]
export const zAlertStatus = z.enum(ALERT_STATUSES)

// warning → brass tone, critical → rust tone.
export const ALERT_SEVERITIES = ['warning', 'critical'] as const
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number]
export const zAlertSeverity = z.enum(ALERT_SEVERITIES)

// Who closed a resolved alert; null while open.
export const ALERT_RESOLVERS = ['property', 'admin'] as const
export type AlertResolver = (typeof ALERT_RESOLVERS)[number]
export const zAlertResolver = z.enum(ALERT_RESOLVERS)

export const zAlert = z.object({
  id: zId,
  propertyId: zId,
  propertyName: z.string(),
  propertySlug: z.string().nullable().default(null),
  // Stable machine key chosen by the property (e.g. catalog.airalo.uncovered_plans).
  key: z.string(),
  status: zAlertStatus,
  severity: zAlertSeverity,
  title: z.string(),
  message: z.string().nullable().default(null),
  // Free JSON object reported by the property; rendered compactly as key: value.
  details: zJsonBlob.nullable().default({}),
  occurrences: z.number().default(1),
  firstSeenAt: zIsoDate,
  lastSeenAt: zIsoDate,
  resolvedAt: zIsoDate.nullable().default(null),
  resolvedBy: zAlertResolver.nullable().default(null),
})
export type Alert = z.infer<typeof zAlert>

// GET /alerts → { alerts: [...], meta }, ordered lastSeenAt desc.
export const zAlertList = listSchema('alerts', zAlert)
export type AlertList = z.infer<typeof zAlertList>

// GET /alerts/summary → { summary: { open, critical, warning } } (open alerts only).
export const zAlertSummary = z.object({
  open: z.number().default(0),
  critical: z.number().default(0),
  warning: z.number().default(0),
})
export type AlertSummary = z.infer<typeof zAlertSummary>
